import type { ReactNode } from "react";
import { AlertTriangle, CheckCircle2, Info, XCircle, X } from "lucide-react";

type Tone = "critical" | "warning" | "info" | "success";

const TONES: Record<Tone, { box: string; Icon: typeof Info }> = {
  critical: {
    box: "border-red-500/30 bg-red-500/10 text-red-800 dark:text-red-300",
    Icon: XCircle,
  },
  warning: {
    box: "border-amber-400/40 bg-amber-50 text-amber-900 dark:border-amber-700/40 dark:bg-amber-500/10 dark:text-amber-200",
    Icon: AlertTriangle,
  },
  info: {
    box: "border-sky-400/30 bg-sky-50 text-sky-900 dark:border-sky-700/40 dark:bg-sky-500/10 dark:text-sky-200",
    Icon: Info,
  },
  success: {
    box: "border-green-500/30 bg-green-500/10 text-green-800 dark:text-green-300",
    Icon: CheckCircle2,
  },
};

type Props = {
  tone?: Tone;
  title?: ReactNode;
  children?: ReactNode;
  /** Shows a close button when given. */
  onDismiss?: () => void;
  dismissLabel?: string;
};

/**
 * An inline message box — the Tailwind stand-in for Polaris's Banner, in the
 * same four tones. `role="alert"` for errors so a screen reader announces them.
 */
export default function Notice({
  tone = "info",
  title,
  children,
  onDismiss,
  dismissLabel = "Dismiss",
}: Props) {
  const { box, Icon } = TONES[tone];
  return (
    <div
      role={tone === "critical" ? "alert" : "status"}
      className={`flex gap-2 rounded-lg border px-3 py-2 text-sm ${box}`}
    >
      <Icon className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <div className="min-w-0 flex-1 space-y-1">
        {title ? <p className="font-semibold">{title}</p> : null}
        {children}
      </div>
      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={dismissLabel}
          className="shrink-0 opacity-70 hover:opacity-100"
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}
